import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const APP_NAME = 'Nurture Buddy.ai';

const PRIVACY_CONTENT = (businessName) => `
This Privacy Policy describes how ${businessName} ("we," "our," or "us") uses Nurture Buddy.ai to send you SMS messages and how your information is handled.

Information we collect
When you submit our public contact form or receive text messages from ${businessName} through Nurture Buddy.ai, we may use your name, email, mobile number, and message history to respond to your inquiry and send messages you have opted in to receive.

How we use your information
We use your contact information only to respond to your inquiry and to send SMS messages you have voluntarily agreed to receive, such as real estate updates, check-ins, and appointment reminders. We do not sell or share your mobile number with third parties or affiliates for marketing or promotional purposes.

SMS opt-in
SMS consent is optional on our contact form. You may submit the form without agreeing to receive SMS. If you check the optional SMS consent box, you agree to receive recurring marketing SMS from ${businessName}. Message frequency varies. Message and data rates may apply. Reply STOP to opt out at any time. Reply HELP for help.

Data handling
Messaging is powered by Nurture Buddy.ai and its service providers (including Twilio) for delivery. Your data is used only for the purposes described above and in accordance with applicable privacy laws.

Opt-out
You can opt out of SMS at any time by replying STOP to any message. Reply HELP for help or more information.

Contact
For questions about this privacy policy or your data, please contact ${businessName} directly.

Last updated: ${new Date().toISOString().slice(0, 10)}
`;

const TERMS_CONTENT = (businessName) => `
These Terms of Service apply to the SMS messaging you receive from ${businessName} via Nurture Buddy.ai.

Consent
SMS consent is optional on our public contact form. If you check the optional SMS consent checkbox, you consent to receive recurring marketing SMS from ${businessName} for purposes such as real estate updates, follow-ups, and appointment reminders. Consent is not a condition of purchase or use of our services.

Message frequency
Message frequency varies. You may receive periodic check-ins, market updates, and appointment-related messages.

Opt-out
You can unsubscribe at any time by replying STOP. Reply HELP for support. Message and data rates may apply.

Service
Messaging is provided through Nurture Buddy.ai. ${businessName} is responsible for the content of messages sent to you. Nurture Buddy.ai and its providers are not responsible for the content of individual messages.

Contact
For questions about these terms or the messages you receive, please contact ${businessName} directly.

Last updated: ${new Date().toISOString().slice(0, 10)}
`;

function PolicyPage() {
  const { agentId } = useParams();
  const location = useLocation();
  const pathname = location?.pathname || '';
  const isPrivacy = pathname.endsWith('/privacy');
  const isTerms = pathname.endsWith('/terms');
  const valid = isPrivacy || isTerms;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agentId || !valid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fallback = { fullName: 'This business', businessName: 'This business' };
    fetch(`/api/p/public-profile?agentId=${agentId}`)
      .then((res) => (res.ok ? res.json() : fallback))
      .then((data) => {
        if (!cancelled) setProfile(data || fallback);
      })
      .catch(() => {
        if (!cancelled) setProfile(fallback);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [agentId, valid]);

  if (!valid) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <p className="text-muted-foreground">Page not found.</p>
      </div>
    );
  }

  const businessName = profile?.businessName || profile?.fullName || 'This business';
  const title = isPrivacy
    ? `Privacy Policy – ${businessName}`
    : `Terms of Service – ${businessName}`;
  const content = isPrivacy ? PRIVACY_CONTENT(businessName) : TERMS_CONTENT(businessName);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">
          {loading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-2">
                {businessName} uses {APP_NAME} to send you messages.
              </p>
              <h1 className="text-2xl font-bold mb-6">
                {isPrivacy ? 'Privacy Policy' : 'Terms of Service'}
              </h1>
              <div className="prose prose-sm max-w-none text-foreground/90 whitespace-pre-wrap">
                {content.trim()}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default PolicyPage;
